import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  BookOpen,
  Clock,
  Quote,
  Lightbulb,
  Sparkles,
  Microscope,
} from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';

const SPURGEON_DEMO = {
  video_title: 'Compel Them to Come In',
  preacher: 'Charles Haddon Spurgeon',
  key_themes: [
    'The Urgency of Evangelism',
    "God's Grace for All",
    'The Great Commission',
    'Compassion for the Lost',
    'Divine Hospitality',
  ],
  summary: `In this landmark 1858 sermon preached at the Music Hall, Royal Surrey Gardens, Charles Spurgeon expounds Luke 14:23 — the parable of the great supper — with characteristic passion and evangelistic fervour. The sermon is widely regarded as one of the greatest evangelistic messages ever delivered in the English language, and it remains as piercing today as the night it was first preached.\n\nSpurgeon opens by setting the scene of God's great banquet: a feast of infinite grace has been prepared, yet those first invited — the self-righteous and the religiously comfortable — made excuses and refused to come. The master then commands his servant to go into the highways and hedges and compel the poor, the maimed, the halt, and the blind to fill his house. Spurgeon applies every element of the parable with precision: the feast is the gospel, the servant is every preacher and believer, and the highways and hedges represent the forgotten margins of society.\n\nThe sermon's centrepiece is Spurgeon's masterly exposition of the word "compel." This is not physical force, but the irresistible urgency of love — the earnest, tearful, passionate pleading of one who understands what is at stake for an immortal soul. He calls every Christian to become a missionary in their own sphere, and closes with a direct, searching appeal to the unconverted members of his congregation, urging them to abandon every excuse and come to Christ that very night.`,
  scripture_references: [
    {
      book: 'Luke',
      chapter: 14,
      verse: '23',
      context:
        'The main text — "Go out into the highways and hedges, and compel them to come in, that my house may be filled."',
    },
    {
      book: 'Isaiah',
      chapter: 55,
      verse: '1',
      context:
        'The universal gospel invitation — "Ho, every one that thirsteth, come ye to the waters, and he that hath no money."',
    },
    {
      book: 'Romans',
      chapter: 10,
      verse: '14',
      context:
        'The necessity of preaching — "How then shall they call on him in whom they have not believed? And how shall they believe in him of whom they have not heard?"',
    },
    {
      book: '2 Corinthians',
      chapter: 5,
      verse: '20',
      context:
        'The ambassador\'s role — "We are ambassadors for Christ, as though God did beseech you by us: we pray you in Christ\'s stead, be ye reconciled to God."',
    },
    {
      book: 'Revelation',
      chapter: 22,
      verse: '17',
      context:
        'The standing invitation — "The Spirit and the bride say, Come. And let him that heareth say, Come. And let him that is athirst come."',
    },
    {
      book: 'Matthew',
      chapter: 9,
      verse: '37-38',
      context:
        'The harvest and the labourers — "The harvest truly is plenteous, but the labourers are few; pray ye therefore the Lord of the harvest."',
    },
    {
      book: 'Luke',
      chapter: 15,
      verse: '4',
      context:
        'The parable of the lost sheep — the shepherd leaves the ninety-nine to seek the one that is lost.',
    },
    {
      book: 'Ezekiel',
      chapter: 33,
      verse: '11',
      context:
        '"As I live, saith the Lord God, I have no pleasure in the death of the wicked; but that the wicked turn from his way and live."',
    },
  ],
  timestamps: [
    {
      time: 0,
      label: 'Introduction: The Great Supper',
      description:
        'Spurgeon introduces the parable and its context — a feast of infinite grace prepared by God, and the inexcusable refusal of the first guests.',
    },
    {
      time: 480,
      label: 'Those Who Made Excuses',
      description:
        'Three classes of people who refused the invitation, and their modern counterparts — the busy, the pleasure-seeking, and the domestically distracted.',
    },
    {
      time: 1020,
      label: '"Go Out" — The Command to Evangelise',
      description:
        'The master sends his servants beyond the walls. The church must carry the gospel outward, not merely wait for the world to wander in.',
    },
    {
      time: 1680,
      label: '"Highways and Hedges" — Seeking the Outcasts',
      description:
        'The gospel is especially for those the world has cast aside — the poor, the broken, the wandering. No one is too far gone for grace.',
    },
    {
      time: 2280,
      label: '"Compel Them" — The Urgency of Love',
      description:
        'What does it mean to compel? Not force, but the earnest, tearful, loving pleading of one who knows that eternal life and eternal death hang in the balance.',
    },
    {
      time: 3000,
      label: 'Every Christian a Missionary',
      description:
        'Spurgeon charges every believer to be a soul-winner in their own sphere and station in life — the pew is as much a mission field as the pulpit.',
    },
    {
      time: 3600,
      label: 'Direct Appeal to the Unconverted',
      description:
        'Spurgeon turns from the congregation as a whole and addresses unconverted sinners directly — a searching and tender closing appeal to come to Christ that very night.',
    },
  ],
  key_quotes: [
    'If men will not come to Christ, we must go to them. The mountain will not come to Mahomet, and so Mahomet must go to the mountain. Since sinners will not come to the mercy-seat, we must carry the mercy-seat to them.',
    'Can you look upon a multitude going to destruction without emotion? If so, I fear that you have never truly tasted the grace of God yourself, for he that has been forgiven much will love much.',
    'Compel them to come in — not with the strong arm of outward force, but with the invincible force of love, the omnipotence of earnest entreaty, the constraining power of a tearful eye and a broken heart.',
    'Every converted man should be a missionary. If he is not, I question seriously whether he has been truly converted at all, or whether he has received the Spirit of Christ.',
    'God is not willing that any should perish. The feast is prepared. The table is spread. Room — there is room — boundless room in the great heart of God for every one of you who will come tonight.',
    'Oh, sinner! I would weep over you if I could. I would plead with you on my knees. Nothing stands between you and the feast of grace but your own proud refusal to accept what God so freely gives.',
  ],
  application_points: [
    'Examine your evangelistic life honestly: Are you actively going out — to your neighbours, colleagues, and family — with the gospel invitation, or are you passively waiting for others to come to you?',
    'Let urgency govern your witness. Spurgeon reminds us that souls are perishing daily. Speak of Christ with the earnestness of one who genuinely believes in eternity.',
    'Seek the overlooked and the outcast. The highways and hedges of your city are full of people the church has not yet reached — the addicted, the lonely, the bereaved, the forgotten.',
    'Remove every excuse from your own heart today. If you have heard the gospel repeatedly without yet responding, Spurgeon\'s closing appeal applies directly to you: come now, while the door is open.',
    'Pray fervently for labourers. The harvest is plenteous but the labourers are few — intercede specifically for gospel workers, church planters, and missionaries around the world.',
    'Let compassion, not duty, be the engine of your evangelism. Spurgeon wept over sinners. Ask God to break your heart with what breaks his, that your witness might flow from love rather than obligation.',
  ],
};

export default function DemoPage() {
  const [activeSection, setActiveSection] = useState('summary');

  const formatTimestamp = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const sections = [
    { id: 'summary', label: 'Summary', icon: Sparkles },
    { id: 'scriptures', label: 'Scriptures', icon: BookOpen },
    { id: 'timestamps', label: 'Timestamps', icon: Clock },
    { id: 'quotes', label: 'Quotes', icon: Quote },
    { id: 'applications', label: 'Applications', icon: Lightbulb },
  ];

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-14 lg:h-16">
            <Link
              to="/"
              className="flex items-center gap-2 text-stone-600 hover:text-[#1e3a8a]"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              <span className="hidden sm:inline">Back to Home</span>
            </Link>

            <span className="font-serif font-bold text-[#1e3a8a]">SermonSift</span>

            <Link to="/signup">
              <Button className="btn-primary" size="sm">
                Try it free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Demo Banner */}
      <div className="bg-[#1e3a8a] text-white py-3 px-4 text-center text-sm">
        <Microscope className="w-4 h-4 inline-block mr-2 opacity-80" strokeWidth={1.5} />
        <span className="opacity-90">Sample analysis — this is what SermonSift produces from any sermon.</span>
        <Link to="/signup" className="ml-3 underline font-medium hover:opacity-80">
          Sign up free to analyse your own →
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
        <div className="lg:grid lg:grid-cols-[1fr_300px] lg:gap-8">
          {/* Main Content */}
          <main>
            {/* Title Section */}
            <div className="mb-8 animate-fade-in">
              <div className="flex items-center gap-2 text-sm text-stone-500 mb-3">
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-medium text-xs">
                  Sample Analysis · Sermon 227 · Preached 5 December 1858
                </span>
              </div>
              <h1 className="font-serif text-2xl lg:text-3xl font-bold text-[#1e3a8a] mb-2">
                {SPURGEON_DEMO.video_title}
              </h1>
              <p className="text-lg text-stone-600">{SPURGEON_DEMO.preacher}</p>

              {/* Key Themes Tags */}
              <div className="flex flex-wrap gap-2 mt-4">
                {SPURGEON_DEMO.key_themes.map((theme, i) => (
                  <span
                    key={i}
                    className="text-sm bg-[#1e3a8a]/10 text-[#1e3a8a] px-3 py-1 rounded-full"
                  >
                    {theme}
                  </span>
                ))}
              </div>
            </div>

            {/* Mobile Section Navigation */}
            <div className="lg:hidden mb-6">
              <ScrollArea className="w-full">
                <div className="flex gap-2 pb-2">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors ${
                        activeSection === section.id
                          ? 'bg-[#1e3a8a] text-white'
                          : 'bg-white text-stone-600 border border-stone-200'
                      }`}
                    >
                      <section.icon className="w-4 h-4" strokeWidth={1.5} />
                      {section.label}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Summary */}
            <section
              id="summary"
              className={`mb-10 ${activeSection !== 'summary' ? 'hidden lg:block' : ''}`}
            >
              <div className="results-section-header">
                <Sparkles className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                <h3>Summary</h3>
              </div>
              <div className="card p-6">
                <p
                  className="text-stone-700 leading-relaxed whitespace-pre-wrap text-lg"
                  style={{ lineHeight: '1.8' }}
                >
                  {SPURGEON_DEMO.summary}
                </p>
              </div>
            </section>

            {/* Scripture References */}
            <section
              id="scriptures"
              className={`mb-10 ${activeSection !== 'scriptures' ? 'hidden lg:block' : ''}`}
            >
              <div className="results-section-header">
                <BookOpen className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                <h3>Scripture References</h3>
                <span className="text-sm text-stone-500">
                  ({SPURGEON_DEMO.scripture_references.length})
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {SPURGEON_DEMO.scripture_references.map((ref, i) => (
                  <div key={i} className="card p-4">
                    <div className="scripture-badge mb-2">
                      {ref.book} {ref.chapter}:{ref.verse}
                    </div>
                    <p className="text-sm text-stone-600">{ref.context}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Timestamps */}
            <section
              id="timestamps"
              className={`mb-10 ${activeSection !== 'timestamps' ? 'hidden lg:block' : ''}`}
            >
              <div className="results-section-header">
                <Clock className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                <h3>Key Timestamps</h3>
              </div>
              <div className="space-y-3">
                {SPURGEON_DEMO.timestamps.map((ts, i) => (
                  <div key={i} className="card p-4 w-full text-left">
                    <div className="flex items-center gap-4">
                      <span className="timestamp-pill">{formatTimestamp(ts.time)}</span>
                      <div className="flex-1">
                        <p className="font-medium text-stone-800">{ts.label}</p>
                        {ts.description && (
                          <p className="text-sm text-stone-500 mt-1">{ts.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Key Quotes */}
            <section
              id="quotes"
              className={`mb-10 ${activeSection !== 'quotes' ? 'hidden lg:block' : ''}`}
            >
              <div className="results-section-header">
                <Quote className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                <h3>Key Quotes</h3>
              </div>
              <div className="space-y-4">
                {SPURGEON_DEMO.key_quotes.map((quote, i) => (
                  <blockquote key={i} className="quote-block px-6 py-4">
                    "{quote}"
                  </blockquote>
                ))}
              </div>
            </section>

            {/* Application Points */}
            <section
              id="applications"
              className={`mb-10 ${activeSection !== 'applications' ? 'hidden lg:block' : ''}`}
            >
              <div className="results-section-header">
                <Lightbulb className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                <h3>Practical Applications</h3>
              </div>
              <div className="card p-6">
                <ul className="space-y-4">
                  {SPURGEON_DEMO.application_points.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#1e3a8a] text-white text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-stone-700">{point}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Sign Up CTA */}
            <div className="card p-8 text-center bg-[#1e3a8a] text-white mb-10">
              <h3 className="font-serif text-xl font-bold mb-3">
                Ready to analyse your own sermons?
              </h3>
              <p className="text-blue-200 mb-6 max-w-md mx-auto">
                Paste a YouTube link or a transcript and get this full breakdown — summary,
                scriptures, timestamps, quotes, and applications — in under a minute.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/signup">
                  <Button className="bg-white text-[#1e3a8a] hover:bg-stone-100 font-medium px-8">
                    Start free — 5 analyses/month
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button
                    variant="outline"
                    className="border-white/40 text-white hover:bg-white/10 font-medium"
                  >
                    View Pro pricing
                  </Button>
                </Link>
              </div>
            </div>
          </main>

          {/* Desktop Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              {/* Section Navigation */}
              <div className="card p-4 mb-6">
                <h4 className="font-medium text-stone-800 mb-3">Jump to Section</h4>
                <nav className="space-y-1">
                  {sections.map((section) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-stone-600 hover:bg-stone-100 hover:text-[#1e3a8a] transition-colors"
                    >
                      <section.icon className="w-4 h-4" strokeWidth={1.5} />
                      {section.label}
                    </a>
                  ))}
                </nav>
              </div>

              {/* Quick Stats */}
              <div className="card p-4 mb-6">
                <h4 className="font-medium text-stone-800 mb-3">Quick Stats</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-500">Scriptures</span>
                    <span className="font-medium">{SPURGEON_DEMO.scripture_references.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Timestamps</span>
                    <span className="font-medium">{SPURGEON_DEMO.timestamps.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Key Quotes</span>
                    <span className="font-medium">{SPURGEON_DEMO.key_quotes.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Applications</span>
                    <span className="font-medium">{SPURGEON_DEMO.application_points.length}</span>
                  </div>
                </div>
              </div>

              {/* About the Sermon */}
              <div className="card p-4">
                <h4 className="font-medium text-stone-800 mb-3">About This Sermon</h4>
                <div className="space-y-2 text-sm text-stone-600">
                  <p><span className="font-medium text-stone-700">Preacher:</span> C. H. Spurgeon</p>
                  <p><span className="font-medium text-stone-700">Date:</span> 5 December 1858</p>
                  <p><span className="font-medium text-stone-700">Venue:</span> Music Hall, Royal Surrey Gardens, London</p>
                  <p><span className="font-medium text-stone-700">Text:</span> Luke 14:23</p>
                  <p><span className="font-medium text-stone-700">Series:</span> Sermon No. 227</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 border-t border-stone-200 mt-4">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center text-sm text-stone-400">
          <p>SermonSift • A Bethany Labs product</p>
        </div>
      </footer>
    </div>
  );
}
